import api from "./api";
import { normalizeApiError, shouldToastApiError } from '@/app/_module/utils/apiResponseHandler';
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";
import { showToast } from "../utils/show-toast";
import { transformErrorResponse } from "../utils/transform-error-response";

// Quote types
export interface QuoteResult {
  id: string;
  agencyId?: string; // Agency association for multi-agency support
  carrier: string;
  product: string;
  monthlyPremium: number;
  annualPremium: number;
  amBestRating: string;
  eligibility: "Eligible" | "Conditionally Eligible" | "Not Eligible";
  selected?: boolean;
  description?: string;
  coverageAmount?: number;
  termLength?: number;
  features?: string[];
  exclusions?: string[];
  waitingPeriod?: string;
  effectiveDate?: string;
  // Flag to indicate this is a suggested carrier (user doesn't have it selected)
  isSuggested?: boolean;
  // Carrier ID from database
  carrierId?: string;
}

export interface QuoteRequest {
  fullName: string;
  dob: string;
  gender: "Male" | "Female" | "Other";
  height: string;
  weight: string;
  smokerStatus?: "Yes" | "No" | "Former";
  healthClass?: "Preferred Plus" | "Preferred" | "Regular Plus" | "Regular";
  coverageAmount: string;
  paymentFrequency?: "Monthly" | "Quarterly" | "Semi-Annual" | "Annual";
  productType?: "Term Life" | "Whole Life" | "Universal Life" | "Variable Life";
  state: string;
  zipCode: string;
  occupation?: string;
  hobbies?: string[];
  travelFrequency?: string;
  bloodPressure?: string;
  cholesterol?: string;
  smokingHistory?: string;
  familyMedicalHistory?: string;
  existingPolicies?: string[];
}

export interface QuoteResponse {
  data: QuoteResult[];
  total: number;
  generatedAt: string;
  leadId: string;
}

class QuoteService {
  private handleError(error: unknown, operation: string): never {

    const errorResponse = transformErrorResponse(error as Error);
    if (shouldToastApiError(normalizeApiError(error), operation)) showToast(errorResponse.message);
    throw error;
  }

  // Generate quotes for a lead
  async generateQuotes(leadId: string, quoteData: QuoteRequest): Promise<QuoteResult[]> {
    try {
      const response = await handleApiCall(async () => await api.post<{
        statusCode: number;
        message: string;
        data: {
          leadId: string;
          quoteId: string;
          quotes: QuoteResult[];
          generatedAt: string;
          expiresAt?: string;
        };
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.leads.generateQuotes(leadId)),
        body: quoteData,
        auth: true,
      }));

      // Backend returns data.quotes array
      // Check different response structures to handle all cases
      let quotes: QuoteResult[] = [];

      // Case 1: Response is the quotes array directly (handleApiCall already extracted it)
      if (Array.isArray(response)) {
        quotes = response;
      }
      // Case 2: Response.data.quotes (nested structure)
      else if (response?.data?.quotes && Array.isArray(response.data.quotes)) {
        quotes = response.data.quotes;
      }
      // Case 3: Response.quotes (one level nesting)
      else if (response?.quotes && Array.isArray(response.quotes)) {
        quotes = response.quotes;
      }
      // Case 4: Response is an object with data property that contains quotes
      else if (response?.data && Array.isArray(response.data)) {
        quotes = response.data;
      }
      else {
        throw new Error('Invalid response format from generate quotes API');
      }

      showToast("Quotes generated successfully", "success");
      return quotes;
    } catch (error) {
      this.handleError(error, 'generate quotes');
    }
  }

  // Get existing quotes for a lead
  async getQuotes(leadId: string): Promise<QuoteResult[]> {
    try {
      const response = await handleApiCall(async () => await api.get<{
        statusCode: number;
        message: string;
        data: QuoteResult[];
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.leads.getQuotes(leadId)),
        auth: true,
      }));

      // Backend returns data as an array - handleApiCall extracts it
      // Multiple fallback cases to handle different response structures
      if (Array.isArray(response)) {
        // Response is already the quotes array
        return response;
      } else if (response?.data && Array.isArray(response.data)) {
        // Response.data is the quotes array
        return response.data;
      } else if (response?.quotes && Array.isArray(response.quotes)) {
        // Response.quotes is the quotes array
        return response.quotes;
      }

      // If we get here, the response format is unexpected
      console.error('[QuoteService] Invalid getQuotes response:', response);
      throw new Error('Invalid response format from get quotes API');
    } catch (error) {
      this.handleError(error, 'fetch quotes');
    }
  }

  // Save selected quotes
  async saveSelectedQuotes(leadId: string, quoteIds: string[]): Promise<void> {
    try {
      await handleApiCall(async () => await api.post({
        url: getApiUrl(endpoint.leads.saveSelectedQuotes(leadId)),
        body: { quoteIds },
        auth: true,
      }));

      showToast("Selected quotes saved successfully", "success");
    } catch (error) {
      this.handleError(error, 'save selected quotes');
    }
  }

  // Export quotes to PDF
  async exportQuotesToPDF(leadId: string, quoteIds: string[]): Promise<Blob> {
    try {
      const response = await handleApiCall(async () => await api.post<Blob>({
        url: getApiUrl(endpoint.leads.exportQuotesToPDF(leadId)),
        body: { quoteIds },
        auth: true,
        responseType: 'blob',
      }));

      // For blob responses, handleApiCall should return the blob directly
      return response;
    } catch (error) {
      this.handleError(error, 'export quotes to PDF');
    }
  }

  // Send quotes via email
  async sendQuotesByEmail(leadId: string, quoteIds: string[], email: string): Promise<void> {
    try {
      await handleApiCall(async () => await api.post({
        url: getApiUrl(endpoint.leads.sendQuotesByEmail(leadId)),
        body: { quoteIds, email },
        auth: true,
      }));

      showToast("Quotes sent via email successfully", "success");
    } catch (error) {
      this.handleError(error, 'send quotes via email');
    }
  }

  // Test Compulife connection
  async testCompulifeConnection(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await handleApiCall(async () => await api.get<{
        statusCode: number;
        message: string;
        data: any;
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.leads.testCompulifeConnection),
        auth: true,
      }));

      return {
        success: true,
        data: response.data
      };
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Export singleton instance
export const quoteService = new QuoteService();
export default quoteService;
